import type { AnnouncementsProps, LogoProps, PostersProps, DonationQrProps, DonationsProps, EventItem, EventsProps, PrayerTimesProps, ScriptureProps, TickerProps } from '@noorview/core';
import { SLIDE_SPEEDS, addDays, hhmm, parseYmd, scriptureFor } from '@noorview/core';
import { el, setText, sv } from './dom';
import type { Env, WidgetView } from './widgets';
import { prayerBase } from './designs';

/**
 * More designs, so every widget has at least six that each look and work differently. As with the others, they draw
 * with the widget's own colours (--ink, --a, --tile, --line), so styles, colours and fonts apply to all of them.
 */

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const weekday = (date: string) => { const { year, month, day } = parseYmd(date); return new Date(Date.UTC(year, month - 1, day)).getUTCDay(); };
const money = (n: number, cur: string) => `${cur || '£'}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Math.max(0, n))}`;
const amounts = (p: DonationsProps) => { const raised = Math.max(0, Number(p.raised) || 0), goal = Math.max(0, Number(p.goal) || 0); return { raised, goal, pct: goal > 0 ? Math.min(1, raised / goal) : 0 }; };

/* ---------- Prayer times: timeline ---------- */

/** The day's prayers along one line, with the ones already passed filled in and the current or next one marked. */
export const prayerTimeline = (c: HTMLElement): WidgetView => {
  const v = prayerBase(c, 'pt-line', (_k, it) => {
    const rail = el('div', 'pl-rail'); rail.append(el('i', 'pl-dot'));
    it.root.append(it.n, rail, it.s, it.j);
  });
  const grid = c.querySelector<HTMLElement>('.pt2')!;
  const up = v.update;
  // Across when there's width for it; down the side when the widget is tall.
  v.update = (w, env) => { up(w, env); grid.dataset.shape = env.box.width / Math.max(1, env.box.height) < 1.1 ? 'list' : 'row'; };
  return v;
};

/* ---------- Events: agenda, spotlight, days to go ---------- */

const upcoming = (p: EventsProps, day: string) => (p.items ?? []).filter((e) => e.title && e.date >= day).sort((a, b) => (a.date + (a.time ?? '')).localeCompare(b.date + (b.time ?? ''))).slice(0, Math.max(1, p.max || 4));
const dayName = (date: string, today: string) => {
  if (date === today) return 'Today';
  if (date === addDays(today, 1)) return 'Tomorrow';
  const { month, day } = parseYmd(date);
  return `${WEEKDAY[weekday(date)]} ${day} ${MONTH[month - 1]}`;
};

function eventsView(c: HTMLElement, cls: string, render: (list: HTMLElement, items: EventItem[], today: string) => void): WidgetView {
  const label = el('div', 'nvd-label'), list = el('div', cls);
  c.append(label, list);
  let p: EventsProps, key = '', mode: Env['mode'] = 'screen';
  return {
    update(w, env) { p = w.props as EventsProps; mode = env.mode; label.textContent = p.title || 'Upcoming'; key = ''; },
    tick({ day }) {
      if (day === key) return false;
      key = day;
      const items = upcoming(p, day);
      list.replaceChildren();
      if (items.length) render(list, items, day);
      else list.append(el('div', 'nvd-sub nvd-faint', mode === 'screen' ? '' : 'Add events in the inspector'));
      return true;
    },
    destroy() {},
  };
}

/** Grouped under each day, like the list in Apple's Calendar: a day heading, then each event's time and title. */
export const eventsAgenda = (c: HTMLElement) => eventsView(c, 'eva', (list, items, today) => {
  let last = '';
  for (const e of items) {
    if (e.date !== last) { last = e.date; list.append(el('div', 'eva-h', dayName(e.date, today))); }
    const row = el('div', 'eva-i'), body = el('div', 'eva-tx');
    body.append(el('div', 'ev-t', e.title));
    if (e.place) body.append(el('div', 'ev-m', e.place));
    row.append(el('div', 'eva-time', e.time || 'All day'), body);
    list.append(row);
  }
});

/** The next event large, on a calendar page, with what comes after it listed small underneath. */
export const eventsSpotlight = (c: HTMLElement) => eventsView(c, 'evs', (list, items, today) => {
  const [first, ...rest] = items;
  const hero = el('div', 'evs-hero'), page = el('div', 'evs-page'), text = el('div', 'evs-tx');
  const { month, day } = parseYmd(first!.date);
  page.append(el('div', 'evs-mon', MON[month - 1]!), el('div', 'evs-day', String(day)));
  text.append(el('div', 'evs-when nvd-acc', [dayName(first!.date, today), first!.time].filter(Boolean).join(' · ')), el('div', 'ev-t evs-t', first!.title));
  if (first!.place) text.append(el('div', 'ev-m', first!.place));
  hero.append(page, text);
  list.append(hero);
  if (rest.length) {
    const more = el('div', 'evs-more');
    for (const e of rest) {
      const { month: m, day: d } = parseYmd(e.date);
      const r = el('div', 'evs-r');
      r.append(el('span', 'evs-rd', `${d} ${MON[m - 1]}`), el('span', 'ev-t', e.title));
      more.append(r);
    }
    list.append(more);
  }
});

/** How long until each event, in big numbers: "3 days", "Today", "Tomorrow". */
export const eventsDays = (c: HTMLElement) => eventsView(c, 'evy', (list, items, today) => {
  const toN = (d: string) => { const a = parseYmd(d), b = parseYmd(today); return Math.round((Date.UTC(a.year, a.month - 1, a.day) - Date.UTC(b.year, b.month - 1, b.day)) / 86_400_000); };
  for (const e of items) {
    const n = toN(e.date);
    const row = el('div', 'evy-i'), count = el('div', 'evy-n'), body = el('div', 'evy-tx');
    if (n <= 1) count.append(el('b', '', n === 0 ? 'Today' : 'Tmrw'));
    else count.append(el('b', '', String(n)), el('small', '', 'days'));
    const { month, day } = parseYmd(e.date);
    body.append(el('div', 'ev-t', e.title), el('div', 'ev-m', [`${WEEKDAY[weekday(e.date)]!.slice(0, 3)} ${day} ${MON[month - 1]}`, e.time, e.place].filter(Boolean).join(' · ')));
    row.append(count, body);
    list.append(row);
  }
});

/* ---------- News ticker: one at a time, headline band, with the time ---------- */

/** One item at a time, each fading in and holding long enough to read (longer items hold longer). */
export function tickerFade(c: HTMLElement): WidgetView {
  const tk = el('div', 'tk tk-fade'), tag = el('div', 'tk-tag'), win = el('div', 'tkf-win');
  tk.append(tag, win);
  c.append(tk);
  c.style.padding = '0';
  let items: HTMLElement[] = [], i = 0, shownAt = 0, speed = 60;
  const hold = (n: HTMLElement) => Math.max(4, ((n.textContent ?? '').length / Math.max(10, speed)) * 9);
  return {
    update(w) {
      const p = w.props as TickerProps;
      speed = p.speed || 60;
      setText(tag, p.tag || 'News');
      tag.style.display = p.tag === '' ? 'none' : '';
      items = (p.items ?? []).map((x) => x.text).filter(Boolean).map((t) => el('div', 'tkf-i', t));
      win.replaceChildren(...items);
      i = 0; shownAt = 0;
      items[0]?.classList.add('on');
    },
    tick({ now }) {
      if (items.length < 2) return false;
      const t = now.getTime() / 1000;
      if (!shownAt) shownAt = t;
      if (t - shownAt >= hold(items[i]!)) { items[i]!.classList.remove('on'); i = (i + 1) % items.length; items[i]!.classList.add('on'); shownAt = t; }
      return false;
    },
    destroy() {},
  };
}

/** A bold band in the widget's highlight colour, like a breaking-news strip, with the text scrolling across it. */
export function tickerScroll(c: HTMLElement, kind: 'headline' | 'clock'): WidgetView {
  const tk = el('div', `tk tk-${kind}`), win = el('div', 'tk-win'), run = el('div', 'tk-run'), time = el('div', 'tk-time');
  win.append(run);
  tk.append(win);
  if (kind === 'clock') tk.append(time);
  c.append(tk);
  c.style.padding = '0';
  let speed = 60, sig = '';
  const measure = () => {
    const w = [...run.children].slice(0, run.children.length / 2).reduce((s, n) => s + (n as HTMLElement).offsetWidth, 0);
    if (!w) return;
    run.style.setProperty('--tk-d', `${-w}px`);
    run.style.setProperty('--tk-t', `${(w / Math.max(10, speed * 2.2)).toFixed(2)}s`);
    run.classList.add('go');
  };
  return {
    update(w) {
      const p = w.props as TickerProps;
      speed = p.speed || 60;
      const texts = (p.items ?? []).map((x) => x.text).filter(Boolean);
      const s = texts.join('\u0000') + '|' + speed;
      if (s === sig) return;
      sig = s;
      run.classList.remove('go');
      const make = () => texts.map((t) => el('span', '', t));
      run.replaceChildren(...make(), ...make());
      requestAnimationFrame(measure);
    },
    tick({ now }, env) { return kind === 'clock' ? setText(time, hhmm(now, env.masjid.timezone)) : false; },
    destroy() { run.classList.remove('go'); },
  };
}

/* ---------- Announcements: featured, headlines ---------- */

const annItems = (p: AnnouncementsProps) => (p.items ?? []).filter((a) => a.title || a.body);

/** The first announcement large, the rest listed underneath. */
export function announcementsFeatured(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label ann-h lead'), wrap = el('div', 'anf');
  c.append(label, wrap);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      label.textContent = p.title || 'Announcements';
      label.style.display = p.title === '' ? 'none' : '';
      const [first, ...rest] = annItems(p);
      wrap.replaceChildren();
      if (!first) { if (env.mode !== 'screen') wrap.append(el('div', 'nvd-sub nvd-faint', 'Add announcements in the inspector')); return; }
      const hero = el('div', 'anf-hero');
      if (first.title) hero.append(el('div', 'anf-t', first.title));
      if (first.body) hero.append(el('div', 'anf-b', first.body));
      wrap.append(hero);
      if (rest.length) {
        const more = el('div', 'anf-more');
        for (const a of rest) { const r = el('div', 'anf-r'); r.append(el('div', 'ev-t', a.title || a.body)); if (a.title && a.body) r.append(el('div', 'ev-m', a.body)); more.append(r); }
        wrap.append(more);
      }
    },
    tick() { return false; },
    destroy() {},
  };
}

/** Just the headlines, big and bold, one under another. */
export function announcementsHeadlines(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label ann-h lead'), list = el('div', 'anh');
  c.append(label, list);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      label.textContent = p.title || 'Announcements';
      label.style.display = p.title === '' ? 'none' : '';
      const items = annItems(p);
      list.replaceChildren(...items.map((a) => el('div', 'ev-t anh-i', a.title || a.body)));
      if (!items.length && env.mode !== 'screen') list.append(el('div', 'nvd-sub nvd-faint', 'Add announcements in the inspector'));
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Ayah and Hadith: card, side by side, highlight ---------- */

const scriptureTitle = (kind: 'ayah' | 'hadith', p: ScriptureProps) => p.title || (kind === 'ayah' ? 'Ayah of the Day' : 'Hadith');
const refOf = (pick: { reference: string; narrator?: string }, p: ScriptureProps) => [p.showNarrator !== false ? pick.narrator : '', pick.reference].filter(Boolean).join(' · ');

/**
 * card: a heading bar with the reference, then the text, like a page in Apple Books.
 * split: Arabic (or, for hadith, the book) on one side and the translation on the other.
 * highlight: the words highlighted in the widget's colour, as if marked in a book.
 */
export function scriptureMore(c: HTMLElement, kind: 'ayah' | 'hadith', design: 'card' | 'split' | 'highlight'): WidgetView {
  const label = el('div', 'nvd-label'), ar = el('div', 'tx-ar'), tx = el('div', 'tx'), ref = el('div', 'nvd-sub');
  let wrap: HTMLElement;
  if (design === 'card') {
    wrap = el('div', 'sc');
    const bar = el('div', 'sc-bar');
    ref.classList.add('nvd-acc');
    bar.append(label, ref);
    const body = el('div', 'sc-body');
    body.append(ar, tx);
    wrap.append(bar, body);
  } else if (design === 'split') {
    wrap = el('div', 'ss');
    const side = el('div', 'ss-side'), main = el('div', 'ss-main');
    side.append(ar);
    main.append(label, tx, ref);
    wrap.append(side, main);
  } else {
    wrap = el('div', 'nvd-center sh');
    ref.classList.add('nvd-faint');
    wrap.append(label, ar, tx, ref);
  }
  c.append(wrap);
  let p: ScriptureProps, key = '';
  return {
    update(w, env) {
      p = w.props as ScriptureProps;
      label.textContent = scriptureTitle(kind, p);
      if (design === 'split') wrap.classList.toggle('stack', env.box.width / Math.max(1, env.box.height) < 1.3);
      key = '';
    },
    tick({ day }) {
      if (day === key) return false;
      key = day;
      const pick = scriptureFor(kind, p, day);
      const showAr = !!pick.arabic && p.showArabic !== false;
      if (design === 'highlight') { tx.replaceChildren(el('mark', 'sh-m', pick.text ?? '')); } else setText(tx, pick.text ?? '');
      setText(ref, refOf(pick, p));
      if (design === 'split' && !showAr) {
        // Hadith have no Arabic here, so that side names where the hadith is from.
        ar.className = 'ss-book';
        setText(ar, pick.reference.replace(/\s+\d[\d:–-]*$/, '') || scriptureTitle(kind, p));
        ar.style.display = '';
      } else {
        ar.className = 'tx-ar';
        ar.style.display = showAr ? '' : 'none';
        if (showAr) setText(ar, pick.arabic!);
      }
      return true;
    },
    destroy() {},
  };
}

/* ---------- Donation QR: scanner, poster, accent ---------- */

/**
 * scanner: the code inside camera-style corner marks, which says "point your phone here".
 * poster: a big call to action above the code and the web address under it.
 * accent: the code on a white card set on the widget's highlight colour.
 */
export function qrMore(c: HTMLElement, design: 'scanner' | 'poster' | 'accent', qrSvg: (t: string) => string): WidgetView {
  const wrap = el('div', `qm qm-${design}`), box = el('div', 'qr'), label = el('div', 'nvd-label'), title = el('div', 'tx'), cap = el('div', 'nvd-sub'), url = el('div', 'nvd-sub nvd-faint qm-url');
  if (design === 'scanner') {
    const frame = el('div', 'qm-frame');
    frame.append(box, ...['tl', 'tr', 'bl', 'br'].map((k) => el('i', `qm-c ${k}`)));
    wrap.append(label, frame, cap);
  } else if (design === 'poster') {
    wrap.append(title, cap, box, url);
  } else {
    const plate = el('div', 'qm-plate');
    plate.append(box);
    wrap.append(label, plate, cap);
  }
  c.append(wrap);
  let lastUrl = '';
  return {
    update(w) {
      const p = w.props as DonationQrProps;
      label.textContent = p.title || 'Donate';
      title.textContent = p.title || 'Donate';
      cap.textContent = p.caption || '';
      cap.style.display = p.caption ? '' : 'none';
      const u = /^https?:\/\//i.test(p.url) ? p.url : '';
      url.textContent = u.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
      url.style.display = u ? '' : 'none';
      if (u !== lastUrl) { lastUrl = u; box.innerHTML = u ? qrSvg(u) : ''; }
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Posters ---------- */


/** Placeholder posters for the design picker when none are uploaded yet, so each design shows how it looks. */
export const SAMPLE_POSTERS = ['#0A84FF', '#30D158', '#FF9F0A'].map((c, i) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 210 297"><defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c}"/><stop offset="1" stop-color="#111827"/></linearGradient></defs><rect width="210" height="297" fill="url(#g)"/><rect x="24" y="190" width="120" height="14" rx="7" fill="#fff" opacity=".9"/><rect x="24" y="214" width="160" height="9" rx="4.5" fill="#fff" opacity=".55"/><rect x="24" y="230" width="110" height="9" rx="4.5" fill="#fff" opacity=".55"/><circle cx="105" cy="100" r="${40 + i * 6}" fill="#fff" opacity=".18"/></svg>`)}`);

/**
 * framed: on a mount, like a picture in a frame. zoom: each poster drifts slowly closer while it shows.
 * progress: a bar for each poster along the top that fills until the next one. stack: the next posters peek out
 * from behind, like a pile of prints. counter: the poster with "2 of 5" and its name underneath.
 */
export function postersMore(c: HTMLElement, design: 'framed' | 'zoom' | 'progress' | 'stack' | 'counter'): WidgetView {
  const wrap = el('div', `pm pm-${design}`), ps = el('div', 'ps'), bars = el('div', 'pm-bars'), foot = el('div', 'pm-foot'), count = el('span', 'pm-count'), name = el('span', 'pm-name');
  foot.append(count, name);
  wrap.append(ps);
  if (design === 'progress') wrap.append(bars);
  if (design === 'counter') wrap.append(foot);
  c.append(wrap);
  if (design !== 'counter') c.style.padding = '0';
  let imgs: HTMLImageElement[] = [], names: string[] = [], i = 0, last = 0, seconds = 12;
  const show = (k: number) => {
    imgs.forEach((im, j) => {
      im.classList.toggle('on', j === k);
      // In a stack, the next two sit behind the one on show.
      const behind = (j - k + imgs.length) % imgs.length;
      im.dataset.depth = String(design === 'stack' ? Math.min(behind, 3) : 0);
    });
    [...bars.children].forEach((b, j) => { b.classList.toggle('done', j < k); b.classList.toggle('on', j === k); });
    setText(count, imgs.length ? `${k + 1} of ${imgs.length}` : '');
    setText(name, names[k] ?? '');
  };
  return {
    update(w, env) {
      const p = w.props as PostersProps;
      seconds = Math.max(3, p.seconds || 12);
      wrap.style.setProperty('--pm-t', `${seconds}s`);
      // The frame and the stack outline the poster itself, so it fills them (the widget keeps the poster's shape).
      ps.className = `ps fit-${p.fit === 'contain' && design !== 'framed' && design !== 'stack' ? 'contain' : 'cover'}${p.transition === 'slide' ? ' slide' : ''}`;
      let items = (p.items ?? []).filter((x) => x.src);
      if (!items.length && env.mode === 'thumbnail') items = SAMPLE_POSTERS.map((src, k) => ({ id: `s${k}`, src, alt: '' }));
      names = items.map((x) => x.alt ?? '');
      imgs = items.map((x, k) => {
        const img = el('img');
        img.alt = x.alt ?? '';
        img.decoding = 'async';
        img.loading = k < 2 ? 'eager' : 'lazy';
        img.src = x.src;
        return img;
      });
      ps.replaceChildren(...imgs);
      bars.replaceChildren(...imgs.map(() => { const b = el('i'); b.append(el('b')); return b; }));
      bars.style.display = imgs.length > 1 ? '' : 'none';
      i = 0; last = 0;
      show(0);
      if (!imgs.length && env.mode !== 'screen') {
        const e = el('div', 'ps-empty');
        e.append(el('div', '', 'Posters'), el('div', 'nvd-sub', 'Add images in the inspector'));
        ps.append(e);
      }
    },
    tick({ now }) {
      if (imgs.length < 2) return false;
      const t = Math.floor(now.getTime() / 1000);
      if (!last) last = t;
      if (t - last >= seconds) { last = t; i = (i + 1) % imgs.length; show(i); }
      return false;
    },
    destroy() { imgs.forEach((im) => { im.src = ''; }); },
  };
}

/* ---------- Masjid logo ---------- */

/**
 * name: the logo with the masjid's name beside it. stacked: the logo above the name and town. badge: the logo in a
 * circle with a ring in the highlight colour. plate: the logo on a white card (for logos drawn for light backgrounds).
 * wordmark: the masjid's name set large, with no picture.
 */
export function logoMore(c: HTMLElement, design: 'name' | 'stacked' | 'badge' | 'plate' | 'wordmark'): WidgetView {
  const wrap = el('div', `lg lg-d-${design}`), pic = el('div', 'lg-pic'), text = el('div', 'lg-tx'), nm = el('div', 'lg-name'), city = el('div', 'nvd-sub lg-city');
  text.append(nm, city);
  if (design !== 'wordmark') wrap.append(pic);
  if (design === 'name' || design === 'stacked' || design === 'wordmark') wrap.append(text);
  c.append(wrap);
  let src = '';
  return {
    update(w, env) {
      const m = env.masjid;
      wrap.classList.toggle('stack', design === 'name' && env.box.width / Math.max(1, env.box.height) < 1.6);
      setText(nm, m.name || 'Your masjid');
      setText(city, m.city ?? '');
      city.style.display = m.city && design !== 'name' ? '' : 'none';
      pic.classList.toggle('cover', (w.props as LogoProps).fit === 'cover');
      const next = m.logo ?? '';
      if (next === src && pic.firstChild) return;
      src = next;
      if (next) { const img = el('img'); img.alt = m.name; img.decoding = 'async'; img.src = next; pic.replaceChildren(img); }
      else if (env.mode !== 'screen') { const e = el('div', 'lg-empty'); e.append(el('span', '', (m.name || 'M').trim().charAt(0).toUpperCase())); pic.replaceChildren(e); }
      else pic.replaceChildren();
    },
    tick() { return false; },
    destroy() { pic.replaceChildren(); },
  };
}

/* ---------- Posters: slide in and out ---------- */

/**
 * Each poster slides in from an edge of the screen into the widget's place, stays for its time, slides back off,
 * and after the break the next one slides in. The whole widget moves, so while no poster is showing, whatever is
 * underneath is seen. In the Studio editor and the design picker it stays still, so it can be worked on.
 */
export function postersFly(c: HTMLElement): WidgetView {
  const wrap = el('div', 'pm pm-fly'), ps = el('div', 'ps fit-cover');
  wrap.append(ps);
  c.append(wrap);
  c.style.padding = '0';
  const root = () => c.closest<HTMLElement>('.nvd-w');
  let imgs: HTMLImageElement[] = [], i = 0, show = 30, gap = 15, enter: NonNullable<PostersProps['enter']> = 'right', fade = false, dur = 1.6;
  let phase: 'start' | 'in' | 'gap' = 'start', since = 0, live = false;
  /** Just past an edge of the screen. It comes in from `enter` and carries on out through the opposite edge. */
  const OPPOSITE = { left: 'right', right: 'left', top: 'bottom', bottom: 'top' } as const;
  const off = (edge: NonNullable<PostersProps['enter']>) => {
    const r = root(), st = r?.parentElement;
    if (!r || !st) return 'none';
    const pad = 60;
    switch (edge) {
      case 'left': return `translate3d(${-(r.offsetLeft + r.offsetWidth + pad)}px,0,0)`;
      case 'right': return `translate3d(${st.clientWidth - r.offsetLeft + pad}px,0,0)`;
      case 'top': return `translate3d(0,${-(r.offsetTop + r.offsetHeight + pad)}px,0)`;
      default: return `translate3d(0,${st.clientHeight - r.offsetTop + pad}px,0)`;
    }
  };
  // Slide: the widget travels in from the edge of the screen. Fade: it appears and disappears where it is.
  const move = (to: string, animate: boolean) => {
    const r = root();
    if (!r) return;
    const away = to !== 'none';
    // Arriving eases to a stop; leaving starts gently and speeds away.
    const ease = away ? 'cubic-bezier(.55,0,.8,.4)' : 'cubic-bezier(.2,.7,.25,1)';
    r.style.transition = animate ? (fade ? `opacity ${dur}s ease` : `transform ${dur}s ${ease}`) : 'none';
    if (fade) { r.style.transform = ''; r.style.opacity = away ? '0' : '1'; } else { r.style.opacity = ''; r.style.transform = to; }
    if (!animate) void r.offsetWidth;
  };
  const showOnly = (k: number) => imgs.forEach((im, j) => im.classList.toggle('on', j === k));
  const reset = () => { const r = root(); if (r) { r.style.transform = ''; r.style.transition = ''; r.style.opacity = ''; } };
  return {
    update(w, env) {
      const p = w.props as PostersProps;
      show = Math.max(3, p.seconds || 30);
      gap = Math.max(0, p.gap ?? 15);
      enter = p.enter ?? 'right';
      fade = p.transition !== 'slide';
      dur = SLIDE_SPEEDS.find((x) => x.value === p.speed)?.seconds ?? 1.6;
      ps.className = `ps fit-${p.fit === 'contain' ? 'contain' : 'cover'}`;
      let items = (p.items ?? []).filter((x) => x.src);
      if (!items.length && env.mode === 'thumbnail') items = SAMPLE_POSTERS.map((src, k) => ({ id: `s${k}`, src, alt: '' }));
      imgs = items.map((x, k) => { const img = el('img'); img.alt = x.alt ?? ''; img.decoding = 'async'; img.loading = k < 2 ? 'eager' : 'lazy'; img.src = x.src; return img; });
      ps.replaceChildren(...imgs);
      i = 0;
      showOnly(0);
      if (!imgs.length && env.mode !== 'screen') { const e = el('div', 'ps-empty'); e.append(el('div', '', 'Posters'), el('div', 'nvd-sub', 'Add images in the inspector')); ps.append(e); }
      // Only the real screen (and its previews) moves; the editor keeps the poster in place.
      live = env.mode === 'screen' && imgs.length > 0;
      phase = 'start';
      if (!live) reset();
    },
    tick({ now }) {
      if (!live) return false;
      const t = now.getTime() / 1000;
      if (phase === 'start') { move(off(enter), false); requestAnimationFrame(() => move('none', true)); phase = 'in'; since = t; return false; }
      if (phase === 'in' && t - since >= show + dur) { move(off(OPPOSITE[enter]), true); phase = 'gap'; since = t; return false; }
      // The break starts once the poster has gone.
      if (phase === 'gap' && t - since >= gap + dur) {
        i = (i + 1) % imgs.length;
        showOnly(i);
        move(off(enter), false);
        requestAnimationFrame(() => move('none', true));
        phase = 'in'; since = t;
      }
      return false;
    },
    destroy() { reset(); imgs.forEach((im) => { im.src = ''; }); },
  };
}
